import { useGameStore } from '@/store/gameStore'
import { ACADEMY_COURSES, Course } from '@/config/membershipConfig'
import { SectionDeck, StatStrip, EmptyStateRich } from '../components/CourtVision'
import {
  GraduationCap, Power, Lock, BookOpen, Brain, Trophy, Baby, CircleDot,
  type LucideIcon,
} from 'lucide-react'

const COURSE_ICONS: Record<string, LucideIcon> = {
  initiation: GraduationCap,
  technique: BookOpen,
  tactics: Brain,
  elite_training: Trophy,
  kids_camp: Baby,
}

const LEVEL_COLORS: Record<string, string> = {
  beginner: 'var(--success)',
  intermediate: 'var(--warning)',
  advanced: 'var(--cta)',
  elite: 'var(--danger)',
}

const LEVEL_NAMES: Record<string, string> = {
  beginner: 'Principiante',
  intermediate: 'Intermedio',
  advanced: 'Avanzado',
  elite: 'Elite',
}

export function AcademyTabContent() {
  const { courts, activeCourses, courseEnrollments, toggleCourse } = useGameStore()
  const hasCoach = useGameStore(s => s.staff.some(st => st.type === 'coach'))

  const canOfferCourse = (course: Course): { can: boolean; reason?: string } => {
    for (const req of course.requirements) {
      if (req.type === 'staff' && !hasCoach) {
        return { can: false, reason: `Requiere entrenador` }
      }
      if (req.type === 'courts' && courts.length < (req.value as number)) {
        return { can: false, reason: `Minimo ${req.value} pistas` }
      }
    }
    return { can: true }
  }

  const weeklyIncome = activeCourses.reduce((total, courseId) => {
    const course = ACADEMY_COURSES.find(c => c.id === courseId)
    if (!course) return total
    const students = courseEnrollments[courseId] || 0
    return total + course.pricePerSession * students * 5
  }, 0)

  const active = ACADEMY_COURSES.filter(c => activeCourses.includes(c.id))
  const available = ACADEMY_COURSES.filter(c => !activeCourses.includes(c.id) && canOfferCourse(c).can)
  const locked = ACADEMY_COURSES.filter(c => !activeCourses.includes(c.id) && !canOfferCourse(c).can)

  return (
    <div>
      {weeklyIncome > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <StatStrip
            label="Ingresos semanales de la academia"
            value={<span className="money-positive">~{weeklyIncome.toLocaleString('es-ES')}€/sem</span>}
          />
        </div>
      )}

      {active.length > 0 && (
        <SectionDeck title="En marcha" icon={Power} meta={`${active.length} curso${active.length === 1 ? '' : 's'}`}>
          <div className="space-y-2">
            {active.map(course => {
              const students = courseEnrollments[course.id] || 0
              const CourseIcon = COURSE_ICONS[course.id] ?? CircleDot
              return (
                <div key={course.id} className="row-tile" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.55rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <span className="icon-tile tone-success"><CourseIcon size={18} /></span>
                    <div className="row-tile-main">
                      <div className="row-tile-title">
                        {course.name}
                        <span className="pill" style={{ color: LEVEL_COLORS[course.level] }}>{LEVEL_NAMES[course.level]}</span>
                      </div>
                      <div className="row-tile-sub">{course.description}</div>
                    </div>
                    <div className="row-tile-side">
                      <span className="money-positive" style={{ fontWeight: 700 }}>
                        {course.pricePerSession.toLocaleString('es-ES')}€/sesion
                      </span>
                      <button className="icon-btn" onClick={() => toggleCourse(course.id)} style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                        <Power size={11} /> Pausar
                      </button>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                    borderTop: '1px solid var(--border-default)', paddingTop: '0.5rem',
                  }}>
                    <span className="meta-text" style={{ minWidth: '54px' }}>Alumnos</span>
                    <div className="progress-track tall">
                      <div className="progress-fill angled success" style={{ width: `${(students / course.groupSize) * 100}%` }} />
                    </div>
                    <span className="meta-text" style={{ minWidth: '38px', textAlign: 'right', fontWeight: 600 }}>
                      {students}/{course.groupSize}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </SectionDeck>
      )}

      {available.length > 0 && (
        <SectionDeck title="Listos para abrir" icon={GraduationCap}>
          <div className="space-y-2">
            {available.map(course => {
              const CourseIcon = COURSE_ICONS[course.id] ?? CircleDot
              return (
                <button key={course.id} className="row-tile" onClick={() => toggleCourse(course.id)}>
                  <span className="icon-tile tone-cta"><CourseIcon size={18} /></span>
                  <div className="row-tile-main">
                    <div className="row-tile-title">
                      {course.name}
                      <span className="pill" style={{ color: LEVEL_COLORS[course.level] }}>{LEVEL_NAMES[course.level]}</span>
                    </div>
                    <div className="row-tile-sub">{course.description}</div>
                  </div>
                  <div className="row-tile-side">
                    <span className="money-positive" style={{ fontWeight: 700 }}>
                      {course.pricePerSession.toLocaleString('es-ES')}€/sesion
                    </span>
                    <span className="btn btn-success" style={{ padding: '0.3rem 0.7rem', fontSize: '0.78rem' }}>
                      Abrir curso
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        </SectionDeck>
      )}

      {locked.length > 0 && (
        <SectionDeck title="Por desbloquear" icon={Lock}>
          <div className="space-y-2">
            {locked.map(course => {
              const { reason } = canOfferCourse(course)
              const CourseIcon = COURSE_ICONS[course.id] ?? CircleDot
              return (
                <div key={course.id} className="row-tile" style={{ opacity: 0.6 }}>
                  <span className="icon-tile tone-muted"><CourseIcon size={18} /></span>
                  <div className="row-tile-main">
                    <div className="row-tile-title">{course.name}</div>
                    <div className="row-tile-sub">{course.description}</div>
                  </div>
                  <span className="pill pill-warning"><Lock size={10} /> {reason}</span>
                </div>
              )
            })}
          </div>
        </SectionDeck>
      )}

      {active.length === 0 && available.length === 0 && locked.length === 0 && (
        <EmptyStateRich
          icon={GraduationCap}
          title="La academia espera a su primer alumno"
          sub="Contrata un entrenador para empezar a ofrecer cursos"
        />
      )}

      <div className="hint-box">
        Contrata un entrenador para ofrecer cursos. Los alumnos se inscriben progresivamente cada dia.
      </div>
    </div>
  )
}
